import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import useAnimate from '../ui/Motion';
import { InstagramIcon } from '../ui/icons';

const EASE = [0.16, 1, 0.3, 1];

/* ─────────────────────────────────────────────────────────────
   One gallery, one viewer, no media libraries.

   This replaces three third-party pieces that each owned part of the
   job: one laid the grid out, one showed photographs, and a separate
   hand-rolled overlay played clips. Because the photo viewer only ever
   knew about photographs, the two halves of the feed were effectively
   two galleries: you could not arrow from a photo into the clip that
   was posted next to it, and the clips did not share any of the
   viewer's behaviour.

   Here a photograph and a clip are the same kind of thing: one list,
   one index, one set of controls.
   ───────────────────────────────────────────────────────────── */

function PlayBadge() {
    return (
        <span className="tile__play" aria-hidden="true">
            <svg viewBox="0 0 24 24"><path d="M8 5.14v13.72a.5.5 0 00.77.42l10.29-6.86a.5.5 0 000-.84L8.77 4.72a.5.5 0 00-.77.42z" /></svg>
        </span>
    );
}

const Media = React.forwardRef(function Media({ item, active }, videoRef) {

    /* A clip that is no longer on screen must stop making noise.
     *
     * The cleanup is the important half. Detaching a playing <video> from
     * the document does not reliably stop it (Chrome keeps decoding until
     * the element is collected), so closing the viewer left the audio
     * running with nothing on screen to pause. Pausing and dropping the
     * source on the way out is what actually silences it. */
    useEffect(() => {
        const el = videoRef.current;
        if (!el) return undefined;

        if (active) {
            const played = el.play();
            if (played && typeof played.catch === 'function') played.catch(() => {});
        } else {
            el.pause();
        }

        return () => {
            el.pause();
            el.removeAttribute('src');
            /* Forces the element to let go of the stream it had already
               buffered; without it the decode can outlive the element. */
            el.load();
        };
    }, [active]);

    if (item.type === 'video') {
        return (
            /* `src` on the element rather than a <source> child: the
               teardown below has to be able to drop it, and you cannot
               remove an attribute the element never had. */
            <video
                ref={videoRef}
                key={item.id}
                className="viewer__media"
                src={item.src}
                controls
                playsInline
                preload="metadata"
                poster={item.poster}
                onClick={event => event.stopPropagation()}
            />
        );
    }

    return (
        <img
            className="viewer__media"
            src={item.src}
            alt={item.caption || 'Project photograph'}
            onClick={event => event.stopPropagation()}
            draggable={false}
        />
    );
});

export default function MediaGallery({ items, emptyMessage }) {
    const [index, setIndex] = useState(null);
    const animate = useAnimate();
    const dialogRef = useRef(null);
    const returnFocusRef = useRef(null);
    const mediaRef = useRef(null);
    const draggedRef = useRef(false);

    const open = index !== null;
    const item = open ? items[index] : null;

    /* Silence the player on the way out rather than waiting for React to
       unmount it. AnimatePresence keeps the closing subtree alive for the
       length of its exit animation and does not re-render it, so a clip
       carried on playing behind a viewer that had already faded away,
       audible, with nothing on screen to pause. */
    const close = useCallback(() => {
        const el = mediaRef.current;

        if (el && typeof el.pause === 'function') {
            el.pause();
            el.removeAttribute('src');
            el.load();
        }

        setIndex(null);
    }, []);
    const step = useCallback(
        delta => setIndex(i => (i === null ? i : (i + delta + items.length) % items.length)),
        [items.length]
    );

    /* Keyboard is the whole point of a viewer on a desktop: arrows move,
       Escape leaves. */
    useEffect(() => {
        if (!open) return undefined;

        const onKey = event => {
            if (event.key === 'Escape') close();
            else if (event.key === 'ArrowRight') step(1);
            else if (event.key === 'ArrowLeft') step(-1);
        };

        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, close, step]);

    // The page behind must not scroll while the viewer is over it.
    useEffect(() => {
        if (!open) return undefined;

        const { overflow } = document.body.style;
        document.body.style.overflow = 'hidden';

        return () => { document.body.style.overflow = overflow; };
    }, [open]);

    /* Focus moves into the dialog so the keyboard handlers above belong to
       it, and returns to the tile that opened it on the way out. */
    useEffect(() => {
        if (!open) {
            returnFocusRef.current?.focus?.();
            return;
        }

        dialogRef.current?.focus();
    }, [open]);

    /* The next and previous images are decoded before they are asked for,
       so stepping through the feed does not flash an empty frame. */
    useEffect(() => {
        if (!open) return;

        [index + 1, index - 1]
            .map(i => items[(i + items.length) % items.length])
            .filter(next => next && next.type === 'image')
            .forEach(next => { new Image().src = next.src; });
    }, [open, index, items]);

    if (!items.length)
        return <p className="gallery__empty">{emptyMessage}</p>;

    return (
        <>
            <ul className="gallery">
                {items.map((media, i) => (
                    <motion.li
                        key={media.id}
                        className="gallery__cell"
                        initial={animate ? { opacity: 0, y: 18 } : false}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.15 }}
                        transition={{ duration: 0.6, delay: Math.min(i * 0.03, 0.4), ease: EASE }}
                    >
                        <button
                            type="button"
                            className="tile"
                            onClick={event => { returnFocusRef.current = event.currentTarget; setIndex(i); }}
                            aria-label={
                                media.type === 'video'
                                    ? `Play clip${media.caption ? `: ${media.caption.slice(0, 60)}` : ''}`
                                    : `View photograph${media.caption ? `: ${media.caption.slice(0, 60)}` : ''}`
                            }
                        >
                            <img src={media.poster} alt="" loading="lazy" decoding="async" />
                            {media.type === 'video' && <PlayBadge />}
                        </button>
                    </motion.li>
                ))}
            </ul>

            <AnimatePresence>
                {open && (
                    <motion.div
                        /* AnimatePresence needs a key to track the child it
                           is animating out. Without one the exit never
                           resolved, the viewer stayed mounted after it had
                           faded away, and the clip inside it went on
                           playing with nothing on screen. */
                        key="viewer"
                        className="viewer"
                        role="dialog"
                        aria-modal="true"
                        aria-label={item.type === 'video' ? 'Project clip' : 'Project photograph'}
                        ref={dialogRef}
                        tabIndex={-1}
                        onClick={() => { if (!draggedRef.current) close(); }}
                        initial={animate ? { opacity: 0 } : false}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.28, ease: EASE }}
                    >
                        {/* The open item, blurred out of legibility, so the
                            viewer is lit by whatever is being looked at
                            rather than being a flat black sheet. */}
                        <div
                            className="viewer__wash"
                            style={{ backgroundImage: `url(${item.poster})` }}
                            aria-hidden="true"
                        />

                        <button type="button" className="viewer__close" onClick={close} aria-label="Close">
                            &times;
                        </button>

                        {items.length > 1 && (
                            <>
                                <button
                                    type="button"
                                    className="viewer__step viewer__step--prev"
                                    onClick={event => { event.stopPropagation(); step(-1); }}
                                    aria-label="Previous"
                                >&lsaquo;</button>
                                <button
                                    type="button"
                                    className="viewer__step viewer__step--next"
                                    onClick={event => { event.stopPropagation(); step(1); }}
                                    aria-label="Next"
                                >&rsaquo;</button>
                            </>
                        )}

                        {/* The stage is deliberately NOT keyed.
                         *
                         * A keyed motion component nested inside an
                         * AnimatePresence child has to report its own exit
                         * before the parent's can complete. Re-keying this one
                         * on every step left a descendant AnimatePresence was
                         * still waiting on, so closing the viewer faded it to
                         * zero and then never unmounted it: a transparent
                         * full-screen layer stayed over the page and ate every
                         * click. The media inside is keyed instead: a plain
                         * element, which framer does not track. */}
                        <motion.div
                            className="viewer__stage"
                            initial={animate ? { opacity: 0, scale: 0.97 } : false}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.35, ease: EASE }}
                            /* Dragging sideways moves through the feed, which is
                               the gesture a touch screen expects. */
                            drag={items.length > 1 ? 'x' : false}
                            dragConstraints={{ left: 0, right: 0 }}
                            dragElastic={0.18}
                            onDragStart={() => { draggedRef.current = true; }}
                            onDragEnd={(event, info) => {
                                if (info.offset.x < -80) step(1);
                                else if (info.offset.x > 80) step(-1);

                                /* A drag ends with a click, and that click
                                   would otherwise be read as "outside the
                                   media" and close the viewer. */
                                setTimeout(() => { draggedRef.current = false; }, 0);
                            }}
                            /* Deliberately no stopPropagation here. The stage
                               fills the viewport, so swallowing its clicks
                               meant the whole area around the media - almost
                               the entire screen - did nothing. */
                        >
                            <Media key={item.id} ref={mediaRef} item={item} active={open} />
                        </motion.div>

                        <div className="viewer__bar" onClick={event => event.stopPropagation()}>
                            <div className="viewer__meta">
                                <span className="viewer__count">
                                    {index + 1} <i>/</i> {items.length}
                                </span>

                                {item.takenAt && (
                                    <span className="viewer__date">
                                        {item.takenAt.toLocaleDateString(undefined, {
                                            day: 'numeric', month: 'long', year: 'numeric'
                                        })}
                                    </span>
                                )}

                                {item.caption && <p className="viewer__caption">{item.caption}</p>}
                            </div>

                            {item.permalink && (
                                <a
                                    className="viewer__ig"
                                    href={item.permalink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <InstagramIcon aria-hidden="true" />
                                    <span>View on Instagram</span>
                                </a>
                            )}
                        </div>

                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
